import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'
import { Bookmark, Check, FolderPlus, Loader2, X } from 'lucide-react'
import { capturedAdsApi, foldersApi } from '@/services/api'
import type { Folder } from '@/types/shared'

interface Props {
  item: any
  onClose: () => void
  onSaved: () => void
}

/**
 * Guardar una captura en "Mis anuncios" eligiendo carpeta y nota.
 * Antes era un clic a ciegas: todo caía sin carpeta y el aviso no decía dónde buscarlo.
 * Al terminar, el propio modal confirma y ofrece ir a verlo o seguir explorando.
 */
export default function SaveCaptureModal({ item, onClose, onSaved }: Props) {
  const ad = item.adData || {}
  const media = ad.apify_data || {}
  const img0 = media.images?.[0]
  const thumb =
    (typeof img0 === 'string' ? img0 : img0?.url) ||
    media.videos?.[0]?.video_preview_image_url ||
    media.video_preview_image_url ||
    media.page_profile_picture_url
  const hook = ad.ad_creative_bodies?.[0] || ad.ad_creative_link_titles?.[0] || ''

  const [folderId, setFolderId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [newName, setNewName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading: loadingFolders, refetch } = useQuery('folders-flat', foldersApi.getFolders)
  const folders: Folder[] = (data?.folders || []).slice().sort((a: Folder, b: Folder) => a.path.localeCompare(b.path))

  const crear = useMutation((name: string) => foldersApi.createFolder({ name }), {
    onSuccess: async (res: any) => {
      await refetch()
      setFolderId(res.folder._id)
      setNewName(null)
    },
    onError: (err: any) => setError(err?.response?.data?.message || 'No se pudo crear la carpeta')
  })

  const guardar = useMutation(
    () => capturedAdsApi.promote(item._id, { folderId: folderId || undefined, notes: notes.trim() || undefined }),
    {
      onSuccess: onSaved,
      onError: (err: any) => setError(err?.response?.data?.message || 'No se pudo guardar')
    }
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const carpeta = folders.find((f) => f._id === folderId)
  const opcion = (activa: boolean) =>
    `flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
      activa
        ? 'border-amber-500 bg-amber-500/10 text-stone-100'
        : 'border-stone-700 text-stone-300 hover:border-stone-500 hover:bg-stone-800'
    }`

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="guardar-titulo"
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-stone-700 bg-stone-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-stone-800 px-5 py-4">
          <h2 id="guardar-titulo" className="text-lg font-semibold text-stone-100">
            {guardar.isSuccess ? '¡Guardado!' : 'Guardar anuncio'}
          </h2>
          <button onClick={onClose} className="rounded-lg p-1 text-stone-400 hover:bg-stone-800" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-3 border-b border-stone-800 px-5 py-4">
          {thumb ? (
            <img src={thumb} alt="" className="h-14 w-14 flex-shrink-0 rounded-lg object-cover" />
          ) : (
            <div className="h-14 w-14 flex-shrink-0 rounded-lg bg-stone-800" />
          )}
          <div className="min-w-0">
            <p className="truncate font-medium text-stone-100">{item.pageName || 'Anunciante desconocido'}</p>
            {hook && <p className="line-clamp-2 text-sm text-stone-400">{hook}</p>}
          </div>
        </div>

        {guardar.isSuccess ? (
          <div className="flex flex-col items-center gap-4 px-5 py-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
              <Check className="h-7 w-7" />
            </div>
            <p className="text-stone-300">
              Lo tienes en <span className="font-medium text-stone-100">Mis anuncios</span>
              {carpeta ? (
                <>
                  , en la carpeta <span className="font-medium text-stone-100">{carpeta.name}</span>
                </>
              ) : null}
              .
            </p>
            <div className="flex w-full gap-2">
              <button
                onClick={onClose}
                className="flex-1 rounded-lg bg-stone-800 px-4 py-2.5 text-sm text-stone-200 hover:bg-stone-700"
                autoFocus
              >
                Seguir explorando
              </button>
              <Link
                to="/saved-ads"
                className="flex-1 rounded-lg bg-amber-500 px-4 py-2.5 text-center text-sm font-medium text-stone-950 hover:bg-amber-400"
              >
                Ver mis anuncios
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
              <fieldset>
                <legend className="mb-2 text-sm font-medium text-stone-200">¿En qué carpeta lo guardo?</legend>
                <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
                  <button type="button" onClick={() => setFolderId(null)} className={opcion(folderId === null)}>
                    <Bookmark className="h-4 w-4 text-stone-500" />
                    Sin carpeta
                  </button>
                  {loadingFolders && <p className="px-3 py-2 text-sm text-stone-500">Cargando carpetas…</p>}
                  {folders.map((f) => (
                    <button
                      key={f._id}
                      type="button"
                      onClick={() => setFolderId(f._id)}
                      className={opcion(folderId === f._id)}
                      style={{ paddingLeft: `${0.75 + f.level * 1}rem` }}
                    >
                      <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: f.color || '#78716c' }} />
                      <span className="truncate">{f.name}</span>
                    </button>
                  ))}
                </div>

                {newName === null ? (
                  <button
                    type="button"
                    onClick={() => setNewName('')}
                    className="mt-2 flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300"
                  >
                    <FolderPlus className="h-4 w-4" />
                    Crear carpeta nueva
                  </button>
                ) : (
                  <form
                    className="mt-2 flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (newName.trim()) crear.mutate(newName.trim())
                    }}
                  >
                    <input
                      autoFocus
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Nombre de la carpeta"
                      aria-label="Nombre de la carpeta nueva"
                      className="min-w-0 flex-1 rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-sm text-stone-100 placeholder-stone-600 focus:border-amber-500 focus:outline-none"
                    />
                    <button
                      type="submit"
                      disabled={!newName.trim() || crear.isLoading}
                      className="rounded-lg bg-stone-700 px-3 py-2 text-sm text-stone-100 hover:bg-stone-600 disabled:opacity-40"
                    >
                      {crear.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear'}
                    </button>
                  </form>
                )}
              </fieldset>

              <label className="block">
                <span className="mb-2 block text-sm font-medium text-stone-200">
                  Nota <span className="font-normal text-stone-500">(opcional)</span>
                </span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Ej.: buen gancho para probar en mi producto"
                  className="w-full resize-none rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-sm text-stone-100 placeholder-stone-600 focus:border-amber-500 focus:outline-none"
                />
              </label>

              {error && <p className="rounded-lg bg-rose-900/30 px-3 py-2 text-sm text-rose-300">{error}</p>}
            </div>

            <div className="flex gap-2 border-t border-stone-800 px-5 py-4">
              <button
                onClick={onClose}
                className="flex-1 rounded-lg bg-stone-800 px-4 py-2.5 text-sm text-stone-200 hover:bg-stone-700"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setError(null)
                  guardar.mutate()
                }}
                disabled={guardar.isLoading}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium text-stone-950 hover:bg-amber-400 disabled:opacity-60"
              >
                {guardar.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bookmark className="h-4 w-4" />}
                Guardar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
